import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserProfile, UpdateUserResponse, PatchUserResponse, SearchUsersResponse, SearchMeta, SearchUserItem, CreateUserResponse, GetUserResponse } from '../Domain/entity/UserTypes';
import { getUserDetails, updateUser, patchUser, searchUsers, createUser, getUser } from './userActions';

type UserStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
type UpdateStatus = 'idle' | 'saving' | 'saved' | 'failed';
type PatchStatus = 'idle' | 'patching' | 'patched' | 'failed';
type SearchStatus = 'idle' | 'searching' | 'searched' | 'failed';
type CreateStatus = 'idle' | 'creating' | 'created' | 'failed';
type GetUserStatus = 'idle' | 'fetching' | 'fetched' | 'failed';

export type UserState = {
  status: UserStatus;
  updateStatus: UpdateStatus;
  patchStatus: PatchStatus;
  searchStatus: SearchStatus;
  createStatus: CreateStatus;
  getUserStatus: GetUserStatus;
  profile?: UserProfile;
  error?: string;
  updateError?: string;
  patchError?: string;
  searchError?: string;
  createError?: string;
  getUserError?: string;
  lastUpdateResponse?: UpdateUserResponse;
  lastPatchResponse?: PatchUserResponse;
  lastCreateResponse?: CreateUserResponse;
  // 조회된 사용자 (getUser)
  fetchedUser?: GetUserResponse;
  // 검색 결과
  searchResults: SearchUserItem[];
  searchMeta?: SearchMeta;
};

const initialState: UserState = {
  status: 'idle',
  updateStatus: 'idle',
  patchStatus: 'idle',
  searchStatus: 'idle',
  createStatus: 'idle',
  getUserStatus: 'idle',
  searchResults: [],
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState() {
      return initialState;
    },
    clearUserProfile(state) {
      state.profile = undefined;
      state.status = 'idle';
      state.error = undefined;
    },
    clearUpdateStatus(state) {
      state.updateStatus = 'idle';
      state.updateError = undefined;
      state.lastUpdateResponse = undefined;
    },
    clearPatchStatus(state) {
      state.patchStatus = 'idle';
      state.patchError = undefined;
      state.lastPatchResponse = undefined;
    },
    clearSearchResults(state) {
      state.searchStatus = 'idle';
      state.searchError = undefined;
      state.searchResults = [];
      state.searchMeta = undefined;
    },
    clearCreateStatus(state) {
      state.createStatus = 'idle';
      state.createError = undefined;
      state.lastCreateResponse = undefined;
    },
    clearGetUserStatus(state) {
      state.getUserStatus = 'idle';
      state.getUserError = undefined;
      state.fetchedUser = undefined;
    },
  },
  extraReducers: (builder) => {
    // getUserDetails
    builder.addCase(getUserDetails.pending, (state) => {
      state.status = 'loading';
      state.error = undefined;
    });
    builder.addCase(getUserDetails.fulfilled, (state, action: PayloadAction<UserProfile>) => {
      state.status = 'succeeded';
      state.profile = action.payload;
    });
    builder.addCase(getUserDetails.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload ?? '알 수 없는 오류가 발생했습니다.';
    });

    // updateUser
    builder.addCase(updateUser.pending, (state) => {
      state.updateStatus = 'saving';
      state.updateError = undefined;
    });
    builder.addCase(updateUser.fulfilled, (state, action: PayloadAction<UpdateUserResponse>) => {
      state.updateStatus = 'saved';
      state.lastUpdateResponse = action.payload;
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.updateStatus = 'failed';
      state.updateError = action.payload ?? '사용자 정보 저장에 실패했습니다.';
    });

    // patchUser
    builder.addCase(patchUser.pending, (state) => {
      state.patchStatus = 'patching';
      state.patchError = undefined;
    });
    builder.addCase(patchUser.fulfilled, (state, action: PayloadAction<PatchUserResponse>) => {
      state.patchStatus = 'patched';
      state.lastPatchResponse = action.payload;
    });
    builder.addCase(patchUser.rejected, (state, action) => {
      state.patchStatus = 'failed';
      state.patchError = action.payload ?? '사용자 정보 수정에 실패했습니다.';
    });

    // searchUsers
    builder.addCase(searchUsers.pending, (state) => {
      state.searchStatus = 'searching';
      state.searchError = undefined;
    });
    builder.addCase(searchUsers.fulfilled, (state, action: PayloadAction<SearchUsersResponse>) => {
      state.searchStatus = 'searched';
      state.searchResults = action.payload.data;
      state.searchMeta = action.payload.meta;
    });
    builder.addCase(searchUsers.rejected, (state, action) => {
      state.searchStatus = 'failed';
      state.searchError = action.payload ?? '사용자 검색에 실패했습니다.';
      state.searchResults = [];
    });

    // createUser
    builder.addCase(createUser.pending, (state) => {
      state.createStatus = 'creating';
      state.createError = undefined;
    });
    builder.addCase(createUser.fulfilled, (state, action: PayloadAction<CreateUserResponse>) => {
      state.createStatus = 'created';
      state.lastCreateResponse = action.payload;
    });
    builder.addCase(createUser.rejected, (state, action) => {
      state.createStatus = 'failed';
      state.createError = action.payload ?? '사용자 생성에 실패했습니다.';
    });

    // getUser
    builder.addCase(getUser.pending, (state) => {
      state.getUserStatus = 'fetching';
      state.getUserError = undefined;
    });
    builder.addCase(getUser.fulfilled, (state, action: PayloadAction<GetUserResponse>) => {
      state.getUserStatus = 'fetched';
      state.fetchedUser = action.payload;
    });
    builder.addCase(getUser.rejected, (state, action) => {
      state.getUserStatus = 'failed';
      state.getUserError = action.payload ?? '사용자 조회에 실패했습니다.';
    });
  },
});

export const { resetUserState, clearUserProfile, clearUpdateStatus, clearPatchStatus, clearSearchResults, clearCreateStatus, clearGetUserStatus } = userSlice.actions;
export default userSlice.reducer;

